import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { Header } from '@fiji/common/src/types';
import { Add, ChevronRight, Delete, Schedule } from '@mui/icons-material';
import { Checkbox, IconButton, Paper, Stack, Switch, Typography } from '@mui/material';
import { CustomMenu, CustomModal, CustomTable, CustomTransComponent, DateAndYearSkelton, Hierarchy } from 'components';
import { ModalActions } from 'components/CustomModal';
import React, { type JSX } from 'react';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { DisableScheduleModal } from './DisableScheduleModal';
import { useDeleteScheduleMutation, useGetAllSchedulesQuery } from '@fiji/common/src/features/schedule/scheduleApi';
import { useConfirm } from '@fiji/common/src/hooks';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { ScheduleDetails } from './ScheduleDetails';
import { useDebounce, useIsMount, useRBAC } from 'hooks';
import { useFilteredValues } from 'hooks/useFilteredValues';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useGetFilterActionsQuery } from '@fiji/common/src/features/actions/actionsApi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTransformData } from 'hooks/useTransformData';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';

const ActionCell = (data: any, detailsModalRef: any): JSX.Element => (
    <IconButton onClick={(): void => detailsModalRef?.current?.handleScheduleDetails(data)}>
        <ChevronRight />
    </IconButton>
);

const EnabledCell = (data: any, disableModalRef: any, disabled: boolean): JSX.Element => (
    <Switch
        disabled={disabled}
        checked={data?.enabled ?? false}
        onChange={(): void => disableModalRef?.current?.handleDisableModal(data)}
    />
);

const NextScheduleCell = (data: any, convertDateTime: any): any => (
    <>
        {data?.nextRun
            ? `${convertDateTime({
                  timestamp: data?.nextRun,
                  customFormat: 'YYYY/MM/DD',
              })} ${convertDateTime({
                  timestamp: data?.nextRun,
                  customFormat: 'hh:mm A',
              })}`
            : '-'}
    </>
);

const lastRunCell = (data: any, convertDateTime: any): any => (
    <>
        {data?.lastRun
            ? `${convertDateTime({
                  timestamp: data?.lastRun,
                  customFormat: 'YYYY/MM/DD',
              })} ${convertDateTime({
                  timestamp: data?.lastRun,
                  customFormat: 'hh:mm A',
              })}`
            : '-'}
    </>
);

const getExceptionHolidayContent = (data: any): any => {
    if (data?.exceptionHolidayCountry) {
        return 'Holiday';
    }
    return 'Manual';
};

const ExceptionCell = (data: any): any => <>{data?.excludeHolidays?.length ? getExceptionHolidayContent(data) : '-'}</>;

export const ListView = ({ searchKey }: any): JSX.Element => {
    const isGroupStatus = window.location.href?.split('/')?.[4] === 'group';
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const navigate = useNavigate();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const { convertDateTime } = useTransformData();
    const [date, setDate] = React.useState<any>(null);
    const [nextRunDate, setNextRunDate] = React.useState<any>(null);

    const disableModalRef = React.useRef<any>(null);
    const modalId = React.useId();
    const modalRef = React.useRef<any>(null);
    const detailsModalRef = React.useRef<any>(null);
    const [payload, setPayload] = React.useState<any>({ page: 0, size: 10, filters: {} });
    const selectedNode = useTypedSelector((state) => state.common.selectedNode);
    const isMount = useIsMount();
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);

    const canEditSchedule = hasPermission('scheduling-summary_edit-schedules');
    const canCreateSchedule = hasPermission('scheduling-details_create-schedules');
    const canDeleteSchedule = hasPermission('scheduling-details_delete-schedules');

    const frequencyFilterOptions = [
        {
            id: '',
            label: <CustomTransComponent translationKey={'SCHEDULING:ALL'} />,
        },
        {
            id: 'daily',
            label: <CustomTransComponent translationKey={'SCHEDULING:DAILY'} />,
        },
        {
            id: 'weekly',
            label: <CustomTransComponent translationKey={'SCHEDULING:WEEKLY'} />,
        },
        {
            id: 'monthly',
            label: <CustomTransComponent translationKey={'SCHEDULING:MONTHLY'} />,
        },
        {
            id: 'yearly',
            label: <CustomTransComponent translationKey={'SCHEDULING:YEARLY'} />,
        },
        {
            id: 'once',
            label: <CustomTransComponent translationKey={'SCHEDULING:ONCE'} />,
        },
    ];
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    const [deviceData, setDeviceData] = React.useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState([]);
    const [getAllGroups, { data: groupsHierarchy }]: any = useGetAllGroupsMutation();

    const [deleteSchedule, { isLoading: scheduleDeleting }] = useDeleteScheduleMutation();
    const { data: allActions }: any = useGetFilterActionsQuery(
        { body: {}, params: { all: true } },
        {
            refetchOnMountOrArgChange: true,
            skip: !currentOrg?.id,
        }
    );
    const handleDeleteSchedule = async (): Promise<void> => {
        const { error }: any = await deleteSchedule({ scheduleIds: selectedIds });
        if (!error) {
            dispatch(setMessageContent({ isOpen: true, message: t('SCHEDULING:SCHEDULE_DELETED_SUCCESSFULLY') }));
            onCancel(true);
        }
    };

    const { isVisible, onCancel, onClick, onConfirm } = useConfirm(handleDeleteSchedule);

    const [selectedIds, setSelectedIds] = React.useState<any>();

    const tableRef = React.useRef<any>(null);

    const handleFilterChange = (filters: any, sortData?: any): void => {
        if (JSON.stringify(filters) !== JSON.stringify(payload.filters ?? {})) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                filters: { ...filters, ...(isMount ? payload.filters : {}) },
            }));
        }
        if (sortData && Object.keys(sortData)?.length) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                sort: sortData,
            }));
        }
    };

    React.useEffect(() => {
        if (!isMount) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                searchKey: debouncedValue,
            }));
        }
    }, [debouncedValue]);

    React.useEffect(() => {
        if (date) {
            setPayload((prev: any) => ({
                ...prev,
                filters: { ...prev.filters, lastRun: new Date(date)?.getTime() },
            }));
        }
    }, [date]);

    React.useEffect(() => {
        if (nextRunDate) {
            setPayload((prev: any) => ({
                ...prev,
                filters: {
                    ...prev.filters,
                    nextRun: new Date(nextRunDate).getTime(),
                },
            }));
        }
    }, [nextRunDate]);

    React.useEffect(() => {
        if (!deviceData) {
            setDeviceData(groupsHierarchy?.data?.records);
        }
    }, [groupsHierarchy]);

    React.useEffect(() => {
        if (currentOrg?.id) {
            getAllGroups({});
        }
    }, [currentOrg]);

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        setLoadingHierarchyNode((prevState): any => [...prevState, parentData?.id]);
        const { data: childHierarchyData }: any = await getAllGroups({ parent: parentData?.id });
        if (childHierarchyData) {
            setHierarchyMappingData((prevState) => ({
                ...prevState,
                [parentData?.id]: childHierarchyData?.data?.records,
            }));
        }

        setLoadingHierarchyNode((prevState): any => prevState.filter((loadingNode) => loadingNode !== parentData?.id));
    };

    const groupSelectionHandler = (selectedNodeData: any): void => {
        const isGroup = selectedNodeData?.type === 'GROUP';
        const isDevice = selectedNodeData?.type === 'DEVICE';
        const id = selectedNodeData?.id;

        if (!id) return;

        const key = isGroup ? 'groupId' : isDevice ? 'deviceId' : 'gatewayId';
        const itemsClone = [...(payload?.filters?.deviceAndGroup?.[key] ?? [])];
        const index = itemsClone.indexOf(id);

        if (index !== -1) {
            itemsClone.splice(index, 1);
        } else {
            itemsClone.push(id);
        }

        setPayload((prev: any) => {
            const deviceAndGroup = { ...prev?.filters?.deviceAndGroup };
            delete deviceAndGroup[key];
            const filters = {
                ...prev?.filters,
                deviceAndGroup: {
                    ...deviceAndGroup,
                    ...(itemsClone.length && { [key]: itemsClone }),
                },
            };

            if (!Object?.keys(filters?.deviceAndGroup ?? {})?.length) {
                delete filters?.deviceAndGroup;
            }

            return {
                ...prev,
                filters,
                page: 0,
            };
        });
    };

    const actionMenu = (): JSX.Element => <CustomMenu menuList={{ menuGroups: getMenuItems() }} />;

    const getDeviceNameCell = (cell: any, key: string): JSX.Element => {
        if (cell[key]?.length > 2) {
            return <>{`${cell[key][0]?.name},${cell[key]?.[1]?.name} and ${cell[key]?.length - 2} others`}</>;
        }
        return cell[key]?.map((item: any) => item?.name ?? '-');
    };

    const handleFrequencyFilterChange = (filter: any): any => {
        setPayload((prev: any) => {
            const updatedFilters = { ...prev.filters };
            const selectedFrequencies = Array.isArray(prev?.filters?.frequency) ? prev?.filters?.frequency : [];

            if (!filter?.id) {
                delete updatedFilters.frequency;
                return {
                    ...prev,
                    page: 0,
                    filters: updatedFilters,
                };
            }

            const updatedFrequencies = selectedFrequencies.includes(filter?.id)
                ? selectedFrequencies.filter((item: string) => item !== filter?.id)
                : [...selectedFrequencies, filter?.id];

            if (updatedFrequencies.length) {
                updatedFilters.frequency = updatedFrequencies;
            } else {
                delete updatedFilters.frequency;
            }

            return {
                ...prev,
                page: 0,
                filters: updatedFilters,
            };
        });
    };

    const handlePagination = (currentPage: number, pageSize: number): void => {
        setPayload((prev: any) => ({
            ...prev,
            page: currentPage,
            size: pageSize,
        }));
    };
    const getFrequencyFilter = (): JSX.Element => (
        <>
            {frequencyFilterOptions?.map((filter: any) => (
                <Stack key={filter?.id} direction={'row'} alignItems={'center'}>
                    <Checkbox
                        checked={
                            !filter?.id
                                ? !(payload?.filters?.frequency ?? [])?.length
                                : (payload?.filters?.frequency ?? []).includes(filter?.id)
                        }
                        value={filter?.id}
                        onChange={() => handleFrequencyFilterChange(filter)}
                    />
                    <Typography>{filter?.label}</Typography>
                </Stack>
            ))}
        </>
    );

    const getRequiredTableFilters = (filterPayload: any = {}): any => {
        const cloneFilters = JSON.parse(JSON.stringify(filterPayload ?? {}));
        const { deviceAndGroup, ...rest } = cloneFilters;

        if (deviceAndGroup && Object.keys(deviceAndGroup).length > 0) {
            return { ...rest, ...deviceAndGroup };
        }

        if (selectedNode?.wrapperDeviceType === 'Gateway') {
            return { ...rest, gatewayId: [selectedNode.id] };
        }

        if (!selectedNode?.wrapperDeviceType) {
            return { ...rest, groupId: [selectedNode?.id ?? currentOrg?.id] };
        }

        return { ...rest, deviceId: [selectedNode.id] };
    };

    // const getExceptionListCell = (cell: any, key: string): JSX.Element => {
    //     if (cell[key]?.length > 2) {
    //         return <>{`${cell[key][0]?.},${cell[key]?.[1]?.name} and ${cell[key]?.length - 2} others`}</>;
    //     }
    //     return cell[key]?.map((item: any) => item?.name?? '-');
    // };

    const DeleteActions: ModalActions[] = [
        {
            key: 'cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onCancel(true);
            },
        },
        {
            key: 'deleteSchedule',
            label: <CustomTransComponent translationKey={'SCHEDULING:DELETE_SCHEDULE'} />,
            variant: 'contained',
            color: 'error',
            disabled: scheduleDeleting,
            className: 'error-btn',
            handleClick: onConfirm,
        },
    ];

    const getMenuItems = (): any => {
        const menuItems: any = [];
        if (canCreateSchedule) {
            menuItems?.push({
                title: <CustomTransComponent translationKey={'COMMON:ADD'} />,
                icon: <Add />,
                onClick: (): void => {
                    navigate(`/${currentRealmName}/scheduleEvent/${selectedNode?.id ?? currentOrg?.id}`);
                },
            });
        }
        if (canDeleteSchedule) {
            menuItems?.push({
                title: <CustomTransComponent translationKey={'COMMON:DELETE'} />,
                className: !selectedIds?.length ? 'disabled' : '',
                icon: <Delete />,
                onClick: (): void => {
                    onClick();
                },
            });
        }

        return menuItems;
    };

    const getDateHeaderOptions = (): JSX.Element => (
        <DateCalendar value={date ?? null} onChange={(data: any): void => setDate(data)} />
    );

    const getNextRunDateHeaderOptions = (): JSX.Element => (
        <DateCalendar value={nextRunDate ?? null} onChange={(data: any): void => setNextRunDate(data)} />
    );

    const getDeviceHeaderOptions = (): JSX.Element => (
        <Hierarchy
            isLoading={!deviceData}
            data={deviceData ?? []}
            hasChild={(treeItem: any): boolean => treeItem?.groupCount || treeItem?.deviceCount}
            labelKey={'name'}
            hierarchyMappingData={hierarchyMappingData}
            loadingNode={loadingHierarchyNode}
            handleTreeNodeSelection={groupSelectionHandler}
            autoChildSelection={true}
            filters={[
                {
                    key: 'type',
                    operator: '!==',
                    value: 'DEVICE',
                    action: 'hidden',
                },
            ]}
            loadMore={handleFetchHierarchy}
            loadingSkeleton={<GroupHierarchySkeleton row={1} />}
            selectedNodes={[
                ...(payload?.filters?.deviceAndGroup?.groupId ?? []),
                ...(payload?.filters?.deviceAndGroup?.deviceId ?? []),
                ...(payload?.filters?.deviceAndGroup?.gatewayId ?? []),
            ]}
            selectionType={'checkbox'}
        />
    );

    const tableRows: Header[] = [
        {
            header: '',
            width: '5%',
            isSelectable: true,
        },
        {
            header: t('COMMON:ENABLED'),
            width: '10%',
            accessor: 'enabled',
            cell: (data) => EnabledCell(data, disableModalRef, !canEditSchedule),
        },
        {
            header: t('COMMON:NAME'),
            accessor: 'name',
            width: '15%',
            isFilterable: true,
            isDebounce: true,
            isSortable: true,
        },
        {
            header: t('COMMON:DEVICE'),
            width: '15%',
            accessor: 'deviceAndGroup',
            cell: (data: any) => getDeviceNameCell(data, 'deviceIds'),
            isFilterable: (selectedNode?.wrapperDeviceType === 'Gateway' || isGroupStatus) && true,
            paperWrapperClass: 'custom-table-group-width-300',
            typeVariant: 'body2',
            headerOptions: getDeviceHeaderOptions,
            headerSx: (): any => ({
                width: '100%',
            }),
        },
        {
            header: t('COMMON:ACTION'),
            width: '15%',
            accessor: 'actionId',
            cell: (data) => getDeviceNameCell(data, 'actions'),
            isFilterable: true,
            paperWrapperClass: 'custom-table-group-width-300',
            filterOptions:
                allActions?.data?.length > 0
                    ? [{ id: 'all', label: t('SCHEDULING:ALL') }].concat(
                          allActions?.data?.map((action: any) => ({ id: action?.key, label: action?.displayName }))
                      )
                    : [{ id: 'all', label: t('SCHEDULING:ALL') }],
        },
        {
            header: t('SCHEDULING:NEXT_SCHEDULE'),
            width: '15%',
            isSortable: true,
            accessor: 'nextRun',
            cell: (data) => NextScheduleCell(data, convertDateTime),
            isFilterable: true,
            typeVariant: 'body2',
            paperWrapperClass: 'custom-table-group-width-300',
            headerOptions: getNextRunDateHeaderOptions,
            skeleton: <DateAndYearSkelton />,
        },
        {
            header: t('SCHEDULING:LAST_RUN'),
            width: '15%',
            isSortable: true,
            isFilterable: true,
            accessor: 'lastRun',
            typeVariant: 'body2',
            paperWrapperClass: 'custom-table-group-width-300',
            headerOptions: getDateHeaderOptions,
            cell: (data: any): JSX.Element => lastRunCell(data, convertDateTime),
            skeleton: <DateAndYearSkelton />,
        },
        {
            header: t('COMMON:FREQUENCY'),
            width: '10%',
            accessor: 'frequency',
            isSortable: true,
            isFilterable: true,
            headerOptions: getFrequencyFilter,
        },
        {
            header: t('COMMON:EXCEPTION'),
            width: '10%',
            cell: (data: any) => ExceptionCell(data),
        },
        ...(canCreateSchedule && canEditSchedule
            ? [
                  {
                      header: '',
                      width: '5%',
                      extraOptions: actionMenu,
                      accessor: 'actions',
                      cell: (data: any) => ActionCell(data, detailsModalRef),
                  },
              ]
            : []),
    ];

    const normalizeFilters = (filters: any): any => {
        const result: any = {};
        for (const key in filters) {
            const value = filters[key];
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    result[key] = value;
                }
            } else if (value !== undefined && value !== null && value !== '') {
                result[key] = value;
            }
        }

        return result;
    };

    const getModifiedPayload = (filters: any): any => {
        const cloneFilters = JSON.parse(JSON?.stringify(filters ?? {}));

        if (filters?.deviceAndGroup) {
            delete cloneFilters.deviceAndGroup;
            return { ...cloneFilters, ...filters.deviceAndGroup };
        }

        return cloneFilters;
    };

    const [tableFilters] = useFilteredValues({ allFilters: tableRows, triggeredFilters: payload?.filters ?? {} });

    const {
        data: currentData,
        isLoading,
        isFetching,
    } = useGetAllSchedulesQuery(
        {
            ...payload,
            filters: normalizeFilters({
                ...getRequiredTableFilters(tableFilters),
                ...getModifiedPayload(tableFilters),
            }),
        },
        { refetchOnMountOrArgChange: true }
    );

    return (
        <Stack mt={2}>
            <Paper>
                <CustomTable
                    ref={tableRef}
                    data={currentData?.data?.records}
                    headers={tableRows}
                    handleCheckboxSelect={(ids: any) => setSelectedIds(ids)}
                    isPagination
                    handlePageChange={handlePagination}
                    isLoading={isLoading || isFetching}
                    keyToTraverse="id"
                    noDataFoundIcon={<Schedule fontSize="inherit" />}
                    noDataFoundTitle={<CustomTransComponent translationKey={'SCHEDULING:NO_SCHEDULE_ACTIONS'} />}
                    noDataFoundButtonText={<CustomTransComponent translationKey={'SCHEDULING:ADD_SCHEDULE_ACTIONS'} />}
                    total={currentData?.data?.total ?? 10}
                    handleFilterChange={handleFilterChange}
                    noDataFoundDescription={<CustomTransComponent translationKey={'SCHEDULING:ADD_SCHEDULE_ACTIONS'} />}
                />
            </Paper>

            {/* Delete Schedule Action popup */}
            <CustomModal
                key={modalId}
                ref={modalRef}
                isOpen={isVisible}
                isLoading={false}
                type="primary"
                actions={DeleteActions}
                header={<CustomTransComponent translationKey={'SCHEDULING:DELETE_SCHEDULES_ACTION'} />}
                noDividers
                actionsDivider
            >
                <Stack>
                    <Typography variant="body1">
                        <CustomTransComponent translationKey={'SCHEDULING:THE_SCHEDULE_WILL_BE_PERMANENTLY_DELETED'} />
                    </Typography>
                </Stack>
            </CustomModal>

            <DisableScheduleModal key="da4f2gc" ref={disableModalRef} />
            <ScheduleDetails ref={detailsModalRef} />
        </Stack>
    );
};
